import { useEffect } from "react"
import { useEditor, EditorContent, Extension } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { TextStyle } from "@tiptap/extension-text-style"
import { Bold, Italic, List } from "lucide-react"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// Inline FontSize extension — applies font-size as an inline style on the
// TextStyle mark so only selected words are affected, not the whole paragraph.
// This replicates what @tiptap/extension-font-size (Pro) does internally.
// ---------------------------------------------------------------------------
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (size: string) => ReturnType
      unsetFontSize: () => ReturnType
    }
  }
}

const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] }
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el) =>
              el.style.fontSize?.replace(/['"]+/g, "") || null,
            renderHTML: (attrs) =>
              attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run(),
    }
  },
})

// ---------------------------------------------------------------------------

interface RichTextEditorProps {
  content: string
  onChange: (html: string) => void
}

interface ToolbarButtonProps {
  onClick: () => void
  active?: boolean
  title: string
  children: React.ReactNode
}

function ToolbarButton({ onClick, active, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        "px-2 py-1 rounded text-xs font-medium transition-colors",
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {children}
    </button>
  )
}

function Divider() {
  return <div className="w-px h-4 bg-border mx-1 self-center" />
}

export function RichTextEditor({ content, onChange }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit, TextStyle, FontSize],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
  })

  // Sync content when the template loads asynchronously (e.g. first fetch completes)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    if (content !== editor.getHTML()) {
      editor.commands.setContent(content || "<p></p>")
    }
  }, [content, editor])

  const currentFontSize = editor?.getAttributes("textStyle").fontSize ?? null

  return (
    <div className="border border-input rounded-md overflow-hidden focus-within:ring-1 focus-within:ring-ring bg-card">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border bg-muted/50">
        <ToolbarButton
          title="Bold"
          onClick={() => editor?.chain().focus().toggleBold().run()}
          active={editor?.isActive("bold")}
        >
          <Bold size={13} />
        </ToolbarButton>

        <ToolbarButton
          title="Italic"
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          active={editor?.isActive("italic")}
        >
          <Italic size={13} />
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          title="Normal size"
          onClick={() => editor?.chain().focus().unsetFontSize().run()}
          active={!currentFontSize}
        >
          Normal
        </ToolbarButton>

        <ToolbarButton
          title="Large text"
          onClick={() => editor?.chain().focus().setFontSize("1.2em").run()}
          active={currentFontSize === "1.2em"}
        >
          Large
        </ToolbarButton>

        <ToolbarButton
          title="Extra large text"
          onClick={() => editor?.chain().focus().setFontSize("1.5em").run()}
          active={currentFontSize === "1.5em"}
        >
          XL
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          title="Bullet list"
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          active={editor?.isActive("bulletList")}
        >
          <List size={13} />
        </ToolbarButton>
      </div>

      {/* Content area */}
      <EditorContent
        editor={editor}
        className={cn(
          "prose prose-sm max-w-none p-3 text-sm text-foreground",
          "[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-75",
        )}
      />
    </div>
  )
}
